// Clinical Baseline section — extracted to keep NewPatientModal.sections.tsx ≤200 lines.

import type { JSX } from "react";
import { FormField } from "./FormField.js";
import { SelectField } from "./SelectField.js";
import { CheckboxGroupWithOther } from "./CheckboxGroupWithOther.js";
import { SwitchToggle } from "./SwitchToggle.js";
import { CHRONIC_CONDITIONS, APPETITE_OPTS, HOSP_REASON_OPTS } from "../lib/clinical-options.js";
import type { PatientForm } from "./NewPatientModal.sections.js";
import type { PatientFormErrors } from "../lib/patient-validation.js";

type F = PatientForm;
type OnChange = (patch: Partial<F>) => void;
type Errors = PatientFormErrors;

const FALL_HISTORY_OPTS = [
  { value: "none", label: "No falls" }, { value: "one_fall", label: "1 fall" },
  { value: "two_or_more_falls", label: "2+ falls" }, { value: "unknown", label: "Unknown" },
];
const CONTINENCE_OPTS = [
  { value: "continent", label: "Continent" },
  { value: "occasional_incontinence", label: "Occasional incontinence" },
  { value: "frequent_incontinence", label: "Frequent incontinence" },
  { value: "catheterised", label: "Catheterised" },
];
const MOBILITY_OPTS = [
  { value: "independent", label: "Independent" }, { value: "uses_stick", label: "Uses stick" },
  { value: "uses_walker", label: "Uses walker" }, { value: "uses_wheelchair", label: "Uses wheelchair" },
  { value: "bedbound", label: "Bedbound" },
];
const COGNITIVE_OPTS = [
  { value: "alert_oriented", label: "Alert & oriented" }, { value: "mild_forgetfulness", label: "Mild forgetfulness" },
  { value: "moderate_decline", label: "Moderate decline" }, { value: "severe_decline", label: "Severe decline" },
  { value: "diagnosed_dementia", label: "Diagnosed dementia" },
];
const PLAN_OPTS = [
  { value: "remote", label: "Remote" }, { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" }, { value: "gold_b2b", label: "Gold B2B" },
];

export function ClinicalSection({ f, on, e }: { f: F; on: OnChange; e: Errors }): JSX.Element {
  const isDiabetic = f.chronic_conditions.some((c) => c === "diabetes_type1" || c === "diabetes_type2");
  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-4">Clinical baseline</p>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Date of birth" type="date" value={f.date_of_birth} onChange={(v) => on({ date_of_birth: v })} required error={e.date_of_birth} />
        <SelectField label="Falls in prior 12m" value={f.fall_history_prior_12m} onChange={(v) => on({ fall_history_prior_12m: v })} options={FALL_HISTORY_OPTS} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Mobility" value={f.baseline_mobility} onChange={(v) => on({ baseline_mobility: v })} options={MOBILITY_OPTS} required />
        <SelectField label="Cognitive status" value={f.baseline_cognitive_status} onChange={(v) => on({ baseline_cognitive_status: v })} options={COGNITIVE_OPTS} required />
      </div>
      <SelectField label="Baseline appetite" value={f.baseline_appetite} onChange={(v) => on({ baseline_appetite: v })} options={APPETITE_OPTS as unknown as { value: string; label: string }[]} required />
      <SelectField label="Continence (baseline)" value={f.baseline_continence} onChange={(v) => on({ baseline_continence: v })} options={CONTINENCE_OPTS} />
      <FormField label="Primary diagnosis" value={f.primary_diagnosis} onChange={(v) => on({ primary_diagnosis: v })} placeholder="e.g. Type 2 diabetes, hypertension" />
      <div>
        <p className="block text-sm font-medium text-slate-700 mb-2">Chronic conditions</p>
        <CheckboxGroupWithOther
          options={CHRONIC_CONDITIONS as unknown as { value: string; label: string }[]}
          value={f.chronic_conditions}
          onChange={(v) => on({ chronic_conditions: v })}
          scrollable
          otherPlaceholder="e.g. Lupus, Sickle cell anaemia…"
          error={e.chronic_conditions}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Systolic BP (mmHg)" type="number" value={f.baseline_bp_systolic} onChange={(v) => on({ baseline_bp_systolic: v })} placeholder="70–220" />
        <FormField label="Diastolic BP (mmHg)" type="number" value={f.baseline_bp_diastolic} onChange={(v) => on({ baseline_bp_diastolic: v })} placeholder="40–130" />
      </div>
      <FormField label="Heart rate (bpm)" type="number" value={f.baseline_heart_rate} onChange={(v) => on({ baseline_heart_rate: v })} placeholder="40–160" />
      <FormField label="Baseline weight (kg)" type="number" value={f.weight_kg} onChange={(v) => on({ weight_kg: v })} placeholder="e.g. 65.5" />
      <p className="text-xs text-slate-500 -mt-2">Recommended — used for weight-change detection</p>
      {isDiabetic && (
        <FormField label="Baseline blood glucose (mmol/L)" type="number" value={f.baseline_blood_glucose} onChange={(v) => on({ baseline_blood_glucose: v })} placeholder="1–33" />
      )}
      <div className="grid grid-cols-2 gap-3">
        <SelectField label="Plan tier" value={f.plan_tier} onChange={(v) => on({ plan_tier: v })} options={PLAN_OPTS} />
        <FormField label="Enrolment date" type="date" value={f.enrolment_date} onChange={(v) => on({ enrolment_date: v })} required error={e.enrolment_date} />
      </div>
      <SwitchToggle label="Has health insurance?" checked={f.has_health_insurance} onChange={(v) => on({ has_health_insurance: v })} />
      <SwitchToggle label="Paid help at home?" checked={f.household_paid_help_present} onChange={(v) => on({ household_paid_help_present: v })} />
      <SwitchToggle label="Hospitalised in prior 12 months?" checked={f.recent_hospitalisation_prior_12m} onChange={(v) => on({ recent_hospitalisation_prior_12m: v })} />
      {f.recent_hospitalisation_prior_12m && (
        <SelectField label="Recent hospitalisation reason (if any)" value={f.recent_hospitalisation_reason} onChange={(v) => on({ recent_hospitalisation_reason: v })} options={HOSP_REASON_OPTS as unknown as { value: string; label: string }[]} />
      )}
    </div>
  );
}
